import { DS_BREAKPOINT } from '@calm/evexia-design-tokens';
import isSSR from '@calm/web-common/dist/isSSR';
import useConfirmation from '@rhg/hooks/use-confirmation';
import profileMessages from '@rhg/lang/messages/profile';
import useAuth from '@rhg/libs/auth0/use-auth';
import useDatadog from '@rhg/libs/datadog/use-datadog';
import { Box, Theme } from '@rhg/libs/material';
import { LogoutDialog } from '@rhg/libs/material/icons';
import { routes } from '@rhg/utils/routes';
import { useIntl } from 'react-intl';

export const useLogout = () => {
    const { logout } = useAuth();
    const { removeUser } = useDatadog();

    return {
        logout: async () => {
            removeUser();
            sessionStorage.clear();
            logout({
                logoutParams: {
                    returnTo: `${
                        isSSR() ? routes.dashboard : window.location.origin
                    }${routes.dashboard}`,
                },
            });
        },
    };
};

const styleDialogPaper = {
    maxWidth: '100%',
    width: DS_BREAKPOINT.LARGE,
    paddingLeft: { md: 4 },
    paddingRight: { md: 4 },
};

const styleLogoutIconContainer = {
    display: 'flex',
    padding: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '100%',
    backgroundColor: 'secondary.light',
    marginBottom: 4,
    color: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.secondary.main
            : theme.palette.secondary.contrastText,
};
const styleIcon = { width: '35px', height: '35px' };

export function useLogoutConfirmation() {
    const { confirm } = useConfirmation();
    const { logout } = useLogout();
    const { formatMessage } = useIntl();
    const openLogoutDialog = async () => {
        const didConfirm = await confirm({
            title: formatMessage(
                profileMessages['profile.logout_dialog.title']
            ),
            body: formatMessage(
                profileMessages['profile.logout_dialog.confirmText']
            ),
            confirmLabel: formatMessage(
                profileMessages['profile.logout_dialog.confirm']
            ),
            trackDialog: 'ConfirmLogout',
            SimpleDialogProps: {
                PaperProps: {
                    sx: styleDialogPaper,
                },
                bottomActionsWrapperStyle: {
                    marginBottom: { md: 3 },
                },
                iconContainerOverride: (
                    <Box sx={styleLogoutIconContainer}>
                        <LogoutDialog style={styleIcon} />
                    </Box>
                ),
                titleVariant: 'h6',
                bodyTextAlign: 'center',
                titleWrapperStyle: {
                    marginBottom: 2,
                    '.MuiTypography-root': {
                        marginBottom: 0,
                    },
                },
            },
            cancelTrackButton: 'LogoutCancel',
            confirmTrackButton: 'LogoutConfirm',
        });
        if (didConfirm) {
            await logout();
        }
        return didConfirm;
    };

    return { openLogoutDialog };
}

export default useLogout;
